import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  calculerTotauxFacture,
  TAUX_TVA_DEFAUT,
  type LigneFactureInput,
  type Product,
} from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Combobox, type OptionCombobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/field';
import { TBody, TD, TH, THead, TR, Table } from '@/components/ui/table';
import { useProduitsPourSelection } from '@/features/stock/api/stock.api';
import { useAntiRebond } from '@/lib/anti-rebond';
import { dh, entier, nombre } from '@/lib/format';

/**
 * Lignes de facture. Choisir un produit du stock renseigne la désignation et le
 * prix, et rattache `product_id` : c'est ce rattachement qui décrémentera le
 * stock à l'émission. Une ligne libre (sans produit) est possible et ne touche
 * pas au stock.
 *
 * Les totaux affichés utilisent exactement la fonction du serveur : l'aperçu ne
 * peut pas diverger de ce qui sera enregistré.
 */
export function LignesFacture({
  lignes,
  onChange,
  lectureSeule,
}: {
  lignes: LigneFactureInput[];
  onChange: (lignes: LigneFactureInput[]) => void;
  lectureSeule?: boolean;
}) {
  const [recherche, setRecherche] = useState('');
  /* Sans anti-rebond, chaque lettre balayait le stock. */
  const rechercheDifferee = useAntiRebond(recherche);
  const { data, isFetching } = useProduitsPourSelection(rechercheDifferee);

  const totaux = calculerTotauxFacture(
    lignes.map((l) => ({
      quantite: l.quantite,
      prix_unitaire_ht: l.prix_unitaire_ht,
      taux_tva: l.taux_tva,
    })),
  );

  const options: OptionCombobox<Product>[] = (data?.data ?? []).map((p) => ({
    cle: p.id,
    libelle: p.nom,
    description: `${entier(p.quantite)} en stock · ${dh(p.prix_vente)} TTC`,
    valeur: p,
  }));

  function ajouterProduit(produit: Product) {
    onChange([
      ...lignes,
      {
        product_id: produit.id,
        designation: produit.nom,
        quantite: 1,
        // Les prix du catalogue sont TTC ; la facture raisonne en HT.
        prix_unitaire_ht: Math.round((produit.prix_vente / (1 + TAUX_TVA_DEFAUT)) * 100) / 100,
        taux_tva: TAUX_TVA_DEFAUT,
      },
    ]);
    setRecherche('');
  }

  function ajouterLigneLibre() {
    onChange([
      ...lignes,
      {
        product_id: null,
        designation: '',
        quantite: 1,
        prix_unitaire_ht: 0,
        taux_tva: TAUX_TVA_DEFAUT,
      },
    ]);
  }

  function modifier(index: number, patch: Partial<LigneFactureInput>) {
    onChange(lignes.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  return (
    <div className="space-y-3">
      {!lectureSeule ? (
        <div className="flex flex-wrap items-end gap-2">
          <Combobox<Product>
            label="Ajouter depuis le stock"
            className="min-w-0 flex-1 sm:max-w-md"
            valeur={recherche}
            chargement={isFetching || recherche !== rechercheDifferee}
            options={options}
            placeholder="Rechercher un produit…"
            videMessage="Aucun produit trouvé"
            onChangeTexte={setRecherche}
            onSelectionner={(o) => ajouterProduit(o.valeur)}
          />
          <Button
            variant="outline"
            onClick={ajouterLigneLibre}
            icone={<Plus aria-hidden className="h-4 w-4" />}
          >
            Ligne libre
          </Button>
        </div>
      ) : null}

      <div className="rounded-md border border-line">
        <Table>
          <THead>
            <TR>
              <TH className="w-20">Qté</TH>
              <TH>Désignation</TH>
              <TH numerique className="w-32">
                Prix HT
              </TH>
              <TH numerique className="w-24">
                TVA
              </TH>
              <TH numerique className="w-32">
                Montant HT
              </TH>
              {!lectureSeule ? <TH className="w-12" /> : null}
            </TR>
          </THead>
          <TBody>
            {lignes.length === 0 ? (
              <TR>
                <TD
                  colSpan={lectureSeule ? 5 : 6}
                  className="py-6 text-center text-sm text-ink-muted"
                >
                  Aucune ligne. Ajoutez un produit du stock ou une ligne libre.
                </TD>
              </TR>
            ) : (
              lignes.map((l, i) => (
                <TR key={`${l.product_id ?? 'libre'}-${i}`}>
                  <TD>
                    {lectureSeule ? (
                      <span className="tabular">{entier(l.quantite)}</span>
                    ) : (
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={String(l.quantite)}
                        onChange={(e) => modifier(i, { quantite: Number(e.target.value) })}
                        className="w-16"
                        aria-label="Quantité"
                      />
                    )}
                  </TD>
                  <TD>
                    {lectureSeule ? (
                      l.designation
                    ) : (
                      <Input
                        value={l.designation}
                        onChange={(e) => modifier(i, { designation: e.target.value })}
                        placeholder="Désignation"
                        aria-label="Désignation"
                      />
                    )}
                  </TD>
                  <TD numerique>
                    {lectureSeule ? (
                      nombre(l.prix_unitaire_ht)
                    ) : (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={String(l.prix_unitaire_ht)}
                        onChange={(e) =>
                          modifier(i, { prix_unitaire_ht: Number(e.target.value) })
                        }
                        className="w-28 text-right"
                        aria-label="Prix unitaire HT"
                      />
                    )}
                  </TD>
                  <TD numerique>
                    {lectureSeule ? (
                      `${Math.round(l.taux_tva * 100)} %`
                    ) : (
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={String(Math.round(l.taux_tva * 100))}
                        onChange={(e) => modifier(i, { taux_tva: Number(e.target.value) / 100 })}
                        className="w-20 text-right"
                        aria-label="Taux de TVA en pourcent"
                      />
                    )}
                  </TD>
                  <TD numerique className="font-medium">
                    {dh(totaux.lignes[i]?.total_ligne_ht ?? 0)}
                  </TD>
                  {!lectureSeule ? (
                    <TD className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Retirer cette ligne"
                        onClick={() => onChange(lignes.filter((_, j) => j !== i))}
                      >
                        <Trash2 aria-hidden className="h-4 w-4 text-danger" />
                      </Button>
                    </TD>
                  ) : null}
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </div>

      <div className="flex justify-end">
        <dl className="w-full max-w-xs space-y-1 rounded-md border border-line bg-surface-sunken p-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-ink-muted">Total HT</dt>
            <dd className="tabular">{dh(totaux.total_ht)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-muted">TVA</dt>
            <dd className="tabular">{dh(totaux.total_tva)}</dd>
          </div>
          <div className="flex justify-between border-t border-line pt-1 font-semibold">
            <dt>Total TTC</dt>
            <dd className="tabular text-mustard-600">{dh(totaux.total_ttc)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
